import React, { useEffect, useRef, useState } from 'react';
import { SearchOutlined, DownOutlined } from '@ant-design/icons';
import { Button, Input, Space, Table, Select, DatePicker, message, Dropdown, Menu, Tag } from 'antd';
import dayjs from 'dayjs';
import Highlighter from 'react-highlight-words';
import axios from 'axios';
import { baseUrl } from '../../config/apiConfig';
import moment from 'moment';
import { Link } from 'react-router-dom';
import tokenStore from '../../stores/TokenStore';

const { RangePicker } = DatePicker;
const transactionStatuses = ['PAYMENT_COMPLETED', 'IN_PROCESS', 'FULL_REFUND', 'PARTIAL_REFUND', '']; // Updated statuses

const TransactionListPage = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 }); // Pagination state

    const [merchants, setMerchants] = useState([]); // State to store merchants from API
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [selectedMerchant, setSelectedMerchant] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [dateRange, setDateRange] = useState([null, null]);

    const searchInput = useRef(null);
    const [statusFilter, setStatusFilter] = useState('PAYMENT_COMPLETED'); // Default status

    // Fetch merchants and map response data
    const fetchMerchants = async () => {
        try {
            const response = await axios.post(baseUrl + '/merchant/select_all_merchant_List', {}, {
                headers: { token: tokenStore.token },
            });
            const merchantList = response.data.data.map((merchant) => ({
                id: merchant.id,
                name: merchant.abbreviatedCompanyName,
            }));
            setMerchants(merchantList);
        } catch (error) {
            message.error('Failed to fetch merchants');
            console.error(error);
        }
    };

    useEffect(() => {
        fetchMerchants(); // Fetch merchants on component load
    }, []);

    // Fetch transactions on search
    const handleSearch = (page = 1, pageSize = 10) => {
        setLoading(true);
        setError(null);

        const requestData = {
            merchantId: selectedMerchant || "",
            pageNum: page,
            pageSize: pageSize,
            status: selectedStatus === "" ? undefined : (selectedStatus || "PAYMENT_COMPLETED"), // Modified this line
            createTimeStart: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : "",
            createTimeEnd: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : "",
        };

        axios.post(baseUrl + "/pay/select_pay_page_v2", requestData, {
            headers: { token: tokenStore.token }, // Replace with your actual token
        })
            .then(response => {
                if (response.data.code === 200) {
                    setTransactions(response.data.data.list);
                    setPagination({
                        current: response.data.data.pageNum,
                        pageSize: response.data.data.pageSize,
                        total: response.data.data.total,
                    });
                } else {
                    setError(response.data.msg || "Failed to fetch data");
                }
            })
            .catch(err => setError("An error occurred: " + err.message))
            .finally(() => setLoading(false));
    };

    // Handle table change for pagination
    const handleTableChange = (pagination) => {
        handleSearch(pagination.current, pagination.pageSize);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        // Same as in your original code for search input
    });

    // Limit the date range selection to 31 days
    const disabledDate = (current) => current && current > dayjs().add(1, 'day').endOf('day');

    const handleDateChange = (dates) => {
        if (dates && dates[1] && dayjs(dates[1]).diff(dates[0], 'day') > 31) {
            message.error('Date range cannot exceed 31 days.');
        } else {
            setDateRange(dates);
        }
    };

    const statusMenu = (
        <Menu onClick={(e) => setStatusFilter(e.key)}>
            <Menu.Item key="All">All</Menu.Item>
            <Menu.Item key="IN_PROCESS">IN_PROCESS</Menu.Item>
            <Menu.Item key="PAYMENT_COMPLETED">PAYMENT_COMPLETED</Menu.Item>
        </Menu>
    );

    const getStatusTagColor = (status) => {
        switch (status) {
            case 'PAYMENT_COMPLETED':
                return 'success';
            case 'IN_PROCESS':
                return 'processing';
            case 'FULL_REFUND':
                return 'warning';
            case 'PARTIAL_REFUND':
                return 'orange';
            default:
                return 'default';
        }
    };

    const columns = [
        {
            title: 'Time of Application',
            dataIndex: 'timeOfApplication',
            key: 'timeOfApplication',
            render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: 'Merchant',
            dataIndex: 'merchantName',
            key: 'merchantName',
            render: (text, record) => record.merchantId ? `${record.abbreviatedCompanyName} (${record.mmerchantId})` : 'N/A',
        },
        {
            title: 'Order No',
            dataIndex: 'vendorOrderNo',
            key: 'vendorOrderNo',
        },
        {
            title: 'Total Amount',
            dataIndex: 'orderAmount',
            key: 'orderAmount',
            render: (amount) => `${(amount / 100).toFixed(2)}`, // Convert from 分 to 元 and format
        },
        {
            title: 'Subtotal Amount',
            dataIndex: 'subtotalAmount',
            key: 'subtotalAmount',
            render: (amount) => `${(amount / 100).toFixed(2)}`, // Convert from 分 to 元 and format
        },
        {
            title: 'Tip',
            dataIndex: 'tipAmount',
            key: 'tipAmount',
            render: (amount) => `${(amount / 100).toFixed(2)}`, // Convert from 分 to 元 and format
        },
        {
            title: 'Transaction Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusTagColor(status)}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Order Details',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Link to={`/transactions/detail/${record.id}`}>Details</Link>
                </Space>
            ),
        },
    ];


    return (
        <div>
            <Space style={{ marginBottom: 16 }}>
                <Select
                    placeholder="Select Merchant"
                    style={{ width: 200 }}
                    onChange={setSelectedMerchant}
                    allowClear
                >
                    {merchants.map((merchant) => (
                        <Select.Option key={merchant.id} value={merchant.id}>
                            {merchant.name}
                        </Select.Option>
                    ))}
                </Select>

                <Select
                    placeholder="Select Transaction Status"
                    style={{ width: 200 }}
                    onChange={setSelectedStatus}
                    defaultValue="PAYMENT_COMPLETED"
                >
                    <Select.Option value="">ALL</Select.Option>
                    {transactionStatuses.filter(status => status !== '').map((status) => (
                        <Select.Option key={status} value={status}>
                            {status}
                        </Select.Option>
                    ))}
                </Select>

                <RangePicker
                    onChange={handleDateChange}
                    disabledDate={disabledDate}
                    style={{ width: 300 }}
                />

                <Button type="primary" onClick={() => handleSearch()}>
                    Search
                </Button>
            </Space>

            <Table
                columns={columns}
                dataSource={transactions}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
            />
        </div>
    );
};

export default TransactionListPage;
